import { useCallback, useEffect, useRef, useState } from "react";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const TERMINAL_STATUSES = new Set(["Completed", "Failed", "Cancelled"]);

const normalizeRun = (row) => ({
  campaignRun_ID: row.campaignRun_ID ?? row.CampaignRun_ID,
  status: row.status ?? row.Status ?? "",
  totalRecipients: row.totalRecipients ?? row.TotalRecipients ?? 0,
  sentCount: row.sentCount ?? row.SentCount ?? 0,
  failedCount: row.failedCount ?? row.FailedCount ?? 0,
  skippedCount: row.skippedCount ?? row.SkippedCount ?? 0,
  errorMessage: row.errorMessage ?? row.ErrorMessage ?? "",
  campaignName: row.campaignName ?? row.CampaignName ?? "",
  channel: row.channel ?? row.Channel ?? "",
});

const getStatusStyle = (status, themeColor) => {
  switch (status) {
    case "Completed":
      return { bg: "rgba(25, 135, 84, 0.15)", color: "#28a745", icon: "fa-solid fa-circle-check" };
    case "Failed":
      return { bg: "rgba(220, 53, 69, 0.15)", color: "#dc3545", icon: "fa-solid fa-circle-xmark" };
    case "Running":
      return { bg: `${themeColor}22`, color: themeColor, icon: "fa-solid fa-spinner fa-spin" };
    case "Queued":
      return { bg: "rgba(255, 193, 7, 0.15)", color: "#ffc107", icon: "fa-solid fa-clock" };
    default:
      return { bg: "rgba(108, 117, 125, 0.2)", color: "#adb5bd", icon: "fa-solid fa-circle" };
  }
};

function CampaignRunStatusModal({ isOpen, onClose, compID, runGuid, campaignName }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const panelBg = isDarkMode ? "#1e2124" : "#eef1f4";
  const panelBorder = isDarkMode ? "#495057" : "#dee2e6";
  const fieldBg = isDarkMode ? "#2d3436" : "#ffffff";
  const mutedText = isDarkMode ? "#adb5bd" : "#6c757d";

  const fetchStatus = useCallback(async () => {
    if (!compID || !runGuid) return null;
    try {
      const response = await axiosInstance.get(APIs.GET_CAMPAIGN_RUN_STATUS(runGuid), {
        params: { CompID: compID },
      });
      const data = response.data?.data ?? response.data?.Data;
      if (data) {
        const normalized = normalizeRun(data);
        setRun(normalized);
        return normalized;
      }
    } catch {
      showErrorToast("Failed to load campaign run status.");
    }
    return null;
  }, [compID, runGuid]);

  useEffect(() => {
    if (!isOpen || !runGuid) return undefined;

    setLoading(true);
    fetchStatus().finally(() => setLoading(false));

    pollRef.current = setInterval(async () => {
      const latest = await fetchStatus();
      if (latest && TERMINAL_STATUSES.has(latest.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (latest.status === "Completed") {
          showSuccessToast(
            `Campaign finished: ${latest.sentCount} sent, ${latest.failedCount} failed, ${latest.skippedCount} skipped.`
          );
        } else if (latest.status === "Failed") {
          showErrorToast(latest.errorMessage || "Campaign run failed.");
        }
      }
    }, 2500);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isOpen, runGuid, fetchStatus]);

  const processed =
    (run?.sentCount ?? 0) + (run?.failedCount ?? 0) + (run?.skippedCount ?? 0);
  const total = run?.totalRecipients ?? 0;
  const progressPct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const statusStyle = getStatusStyle(run?.status, themeColor);
  const displayName = campaignName || run?.campaignName || "Campaign";

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Campaign running"
      subtitle={displayName}
      showButton={true}
      showSubmitButton={false}
      isCentered={true}
      dialogMaxWidth="480px"
    >
      {loading && !run ? (
        <div
          className="d-flex align-items-center justify-content-center gap-2 py-4"
          style={{ color: mutedText }}
        >
          <i className="fa fa-spinner fa-spin" />
          Loading run status…
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          <div
            className="rounded-3 p-3"
            style={{
              backgroundColor: panelBg,
              border: `1px solid ${panelBorder}`,
            }}
          >
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2"
                style={{
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                }}
              >
                <i className={statusStyle.icon} />
                {run?.status ?? "—"}
              </span>
              {run?.channel && (
                <span className="small" style={{ color: mutedText }}>
                  {run.channel}
                </span>
              )}
            </div>

            <div className="mb-2 d-flex justify-content-between small">
              <span style={{ color: mutedText }}>Progress</span>
              <span className="fw-semibold">
                {processed} / {total} ({progressPct}%)
              </span>
            </div>
            <div
              className="rounded-pill overflow-hidden"
              style={{ height: "8px", backgroundColor: fieldBg, border: `1px solid ${panelBorder}` }}
            >
              <div
                role="progressbar"
                style={{
                  width: `${progressPct}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${themeColor} 0%, ${themeColor}cc 100%)`,
                  transition: "width 0.4s ease",
                }}
                aria-valuenow={progressPct}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>

          <div className="d-flex gap-2">
            {[
              { label: "Sent", value: run?.sentCount ?? 0, color: "#28a745" },
              { label: "Failed", value: run?.failedCount ?? 0, color: "#dc3545" },
              { label: "Skipped", value: run?.skippedCount ?? 0, color: mutedText },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex-fill text-center rounded-3 py-2"
                style={{
                  backgroundColor: fieldBg,
                  border: `1px solid ${panelBorder}`,
                }}
              >
                <div
                  className="small text-uppercase fw-semibold"
                  style={{ color: stat.color, fontSize: "0.65rem", letterSpacing: "0.04em" }}
                >
                  {stat.label}
                </div>
                <div className="fw-bold" style={{ fontSize: "1.25rem" }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {run?.errorMessage && (
            <div
              className="rounded-3 px-3 py-2 small"
              style={{
                backgroundColor: "rgba(220, 53, 69, 0.12)",
                border: "1px solid rgba(220, 53, 69, 0.35)",
                color: "#dc3545",
              }}
            >
              {run.errorMessage}
            </div>
          )}

          <p className="mb-0 small" style={{ color: mutedText }}>
            Messages are sent in the background with a short delay between each recipient. You can
            close this dialog; the run will continue.
          </p>
        </div>
      )}
    </CustomModal>
  );
}

export default CampaignRunStatusModal;
